import { Location } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Injectable, isDevMode, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { getRandomId, showErrorSwal } from '@asamco/utils';
import { set } from 'idb-keyval';
import { from, Subject } from 'rxjs';
import { filter, switchMap } from 'rxjs/operators';

import { AsamcoAuthService } from '../auth/asamco-auth.service';
import { AppSettings } from '../configuration/appsettings';
import { ofTypeOld, toPayload } from './iframe-message-rxjs';
import { InnerAppArguments } from './inner-app-arguments.interface';
import { IFrameMessages, MessageType } from './message-type.enum';
import { Message } from './message.interface';

/** Global var hammerhead (TestCafe) is using */
const hammerHeadWindowVar = '%hammerhead%';

/**
 * Service to communicate with inner shell.
 * Utilizes MessageChannel to send and receive messages to the inner app
 */
@Injectable({
	providedIn: 'root',
})
export class IFrameChildService implements OnDestroy {
	private map = new Map();
	/**
	 * internal subject that lives for the lifetime of the app
	 */
	private incomingMessages = new Subject<Message>();
	public incomingMessages$ = this.incomingMessages.asObservable();

	private readonly messageQueue = [];

	private parentOrigin: string;
	private intervalHandleId: ReturnType<typeof setInterval>;

	// Oid of the connected app
	private connectedAppOid: string;

	constructor(private location: Location, private router: Router, private app: AppSettings, private auth: AsamcoAuthService) {}

	public init() {
		const [path, query] = this.location.path().split('?');
		const params = new HttpParams({ fromString: query });
		if (params.has('innerAppArguments')) {
			// TODO: AP-1272 - We could consider moving this into
			// libs/connected-app-login/src/lib/util/pre-bootstrap-main.function.ts
			// This would allow us to never pollute Angulars routing with it.
			// Should be discussed in context with dealing with inner-app-reloads because in such a case
			// this information (the arguments) is lost, and the parentOrigin is needed.
			const innerAppArgumentsJson = params.get('innerAppArguments');
			try {
				const innerAppArguments = JSON.parse(innerAppArgumentsJson) as unknown;
				// should be a app-shell\libs\base\src\lib\iframe\inner-app-arguments.interface.ts
				// @ts-ignore TODO Can we check/validate this?
				this.handleInnerAppArguments(innerAppArguments);
			} catch (error) {
				showErrorSwal(error);
			}
		}

		this.location.replaceState(
			path,
			// We do NOT want to send this back to the outer app to prevent loops.
			// Do this before hooking into router events.
			params.delete('innerAppArguments').delete('asParentUrl').toString(),
			this.location.getState()
		);

		this.router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: NavigationEnd) => {
			this.send({ type: MessageType.updateUrl, payload: { url: e.url } });
		});

		this.intervalHandleId = setInterval(
			(() => {
				if (this.messageQueue.length) {
					this.sendNow(this.messageQueue.pop());
				}
			}).bind(this),
			10
		);

		this.listenToNavigationMessages();

		this.listenToKeyDownEvents();

		this.handleSwalEvents();
	}

	// Todo: move to a separate helper (?)
	private handleSwalEvents() {
		const sendSwalMessage$ = new Subject<boolean>();
		let swalWasOpen = false;

		sendSwalMessage$.subscribe(isOpened => {
			// Only send a message when the state changes
			if (swalWasOpen !== isOpened) {
				// Use sendnow to remove any delay in updating the UI
				if (isOpened) {
					this.sendNow({ type: MessageType.modalOpen, payload: undefined });
				} else {
					this.sendNow({ type: MessageType.modalClose, payload: undefined });
				}
			}
			swalWasOpen = isOpened;
		});

		const observer = new MutationObserver(function (mutations) {
			mutations.forEach(function (mutationRecord) {
				if ((<HTMLBodyElement>mutationRecord.target).classList.contains('swal2-shown')) {
					// Swal opened
					sendSwalMessage$.next(true);
				} else {
					// Swal closed
					sendSwalMessage$.next(false);
				}
			});
		});

		observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
	}

	/**
	 * Handle inner app arguments here, should be called once during initialization.
	 * @param innerAppArguments the inner app arguments for this app.
	 */
	private handleInnerAppArguments(innerAppArguments: InnerAppArguments) {
		this.parentOrigin = innerAppArguments.parentUrl;
		this.app.settings = {
			globalStyle: innerAppArguments.globalStyle,
		};
		this.connectedAppOid = innerAppArguments.appOid;
	}

	public ngOnDestroy(): void {
		this.parentOrigin = null;
		clearInterval(this.intervalHandleId);
	}

	public getConnectedAppOid(): string {
		return this.connectedAppOid;
	}

	public receiveFromWindow(event: MessageEvent) {
		// Allow inner app to recover from reloads of the iframe without reloading the top window
		if (this.parentOrigin === undefined && event?.data?.type === MessageType.userSession) {
			this.parentOrigin = event.origin;
		}
		if (event.origin === this.parentOrigin) {
			this.incomingMessages.next(event.data);
		} else if (isDevMode()) {
			console.warn(`Child app ignored message from origin ${event.origin}, expected ${this.parentOrigin}.`);
			console.log(event);
		}
	}

	private listenToKeyDownEvents() {
		this.incomingMessages$.pipe(ofTypeOld(MessageType.keydown), toPayload()).subscribe(event => {
			document.dispatchEvent(
				new KeyboardEvent('keydown', { key: event.key, ctrlKey: event.ctrlKey, shiftKey: event.shiftKey, altKey: event.altKey })
			);
		});
	}

	private listenToNavigationMessages() {
		this.incomingMessages$.pipe(ofTypeOld(MessageType.navTo), toPayload()).subscribe(m => {
			const url = this.removeDuplicateLegacy(m.url);

			// Get the path from the url
			const path = url.substr(url.indexOf('#!') + 2);

			const currentUrl = this.getCurrentUrl();

			// Remove the current path from the url to get the base url
			const urlWithoutPath = currentUrl.replace(/#.*/, '');

			// Combine the base url with the new path
			const newUrl = `${urlWithoutPath}#!${path}`;

			this.navigateToUrl(newUrl);
		});
	}

	public awaitUserSession() {
		this.send({ type: MessageType.requestUserSession, payload: undefined });

		return this.incomingMessages$.pipe(
			ofTypeOld(MessageType.userSession),
			toPayload(),
			switchMap(userSession => {
				if (userSession.reportingBackend) {
					//userSession.reportingBackend.routingRule = (url: string) => url.startsWith('report');
					userSession.reportingBackend.isPrimary = false;
					this.auth.reportingBackend = userSession.reportingBackend;
				}

				return from(set(`${this.app.settings.appId}_${this.app.settings.userSessionStorageKey}`, JSON.stringify(userSession)));
			})
		);
	}

	private sendNow(message) {
		// Allow inner app to recover from reloads of the iframe without reloading the top window
		if (this.parentOrigin === undefined && message.type === MessageType.requestUserSession) {
			window.parent.postMessage(message, '*');
		} else {
			window.parent.postMessage(message, this.parentOrigin);
		}
	}

	send<T extends keyof IFrameMessages>(message: { type: T; payload: IFrameMessages[T] }) {
		const subject = new Subject<any>();

		const messageToSend = {
			...message,
			payload: {
				...message.payload,
				actionId: getRandomId(),
			},
		};

		this.map.set(messageToSend.payload.actionId, subject);
		this.messageQueue.unshift(messageToSend);
		return subject.toPromise();
	}

	/**
	 * Helper method to fix navigation issues in TestCafe
	 * @param url
	 */
	private navigateToUrl(url: string) {
		/** TestCafe proxy is breaking the url change */
		if (this.isRunningInTestCafe()) {
			window[hammerHeadWindowVar].navigateTo(url);
		} else {
			// Replace the window location (doesn't save the current page to history)
			window.location.replace(url);
		}
	}

	private isRunningInTestCafe() {
		return window[hammerHeadWindowVar] != null;
	}

	/**
	 * Helper function to get the current window url (needed for DevExpress - TestCafe)
	 * @returns
	 */
	private getCurrentUrl() {
		// Hammerhead is a URL-rewriting proxy used by TestCafe and will be injected when creating/running the app in a TestCafe session (more info here: https://github.com/DevExpress/testcafe-hammerhead)
		// The util function below will be used if '%hammerhead%' is defined on the window to get the correct url during TestCafe tests
		if (this.isRunningInTestCafe()) {
			return window[hammerHeadWindowVar].utils.destLocation.getLocation();
		} else {
			return window.location.href;
		}
	}

	/**
	 * Removes dupplicates of string 'legacy/' in url
	 * @param url
	 * @returns
	 */
	private removeDuplicateLegacy(url) {
		return url.replace(/(legacy\/?)+/g, 'legacy/');
	}
}
